import { Menu } from 'lucide-react'
import { useState } from 'react'
import { Link, NavLink, Outlet } from 'react-router'
import { ApiStatus } from '@/components/common/ApiStatus'
import { Logo } from '@/components/common/Logo'
import { ThemeToggle } from '@/components/common/ThemeToggle'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { PUBLIC_NAV } from '@/app/navigation'
import { homeFor, useAuth } from '@/features/auth/store'
import { cn } from '@/lib/utils'

const navClass = ({ isActive }) =>
  cn(
    'rounded-md px-3 py-2 text-sm font-medium transition-colors hover:text-foreground',
    isActive ? 'text-foreground' : 'text-muted-foreground',
  )

export function PublicLayout() {
  const [open, setOpen] = useState(false)
  const { status, user } = useAuth()
  const signedIn = status === 'authenticated'
  const accountLinks = signedIn
    ? [{ to: homeFor(user), label: 'Go to dashboard' }]
    : [{ to: '/login', label: 'Sign in' }]
  return (
    <div className="flex min-h-dvh flex-col">
      <a
        href="#main"
        className="sr-only z-50 rounded-md bg-primary px-4 py-2 text-primary-foreground focus:not-sr-only focus:fixed focus:top-3 focus:left-3"
      >
        Skip to content
      </a>
      <header className="sticky top-0 z-40 border-b bg-background/85 backdrop-blur">
        <div className="mx-auto flex h-16 w-full items-center gap-4 px-4 sm:px-6 lg:px-10 xl:px-16">
          <Logo />
          <nav aria-label="Main" className="ml-6 hidden items-center gap-1 md:flex">
            {PUBLIC_NAV.map((item) => (
              <NavLink key={item.to} to={item.to} className={navClass}>
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
            {signedIn ? (
              <Button asChild className="hidden sm:inline-flex">
                <Link to={homeFor(user)}>Go to dashboard</Link>
              </Button>
            ) : (
              <>
                <Button variant="ghost" asChild className="hidden sm:inline-flex">
                  <Link to="/login">Sign in</Link>
                </Button>
                <Button asChild className="hidden sm:inline-flex">
                  <Link to="/login?next=%2Fonboarding%2Frole%3Fintent%3Dmentor">Become a mentor</Link>
                </Button>
              </>
            )}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open menu">
                  <Menu className="size-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <SheetHeader>
                  <SheetTitle>Menu</SheetTitle>
                </SheetHeader>
                <nav aria-label="Mobile" className="flex flex-col gap-1 px-4">
                  {[...PUBLIC_NAV, ...accountLinks].map((item) => (
                    <NavLink key={item.to} to={item.to} className={navClass} onClick={() => setOpen(false)}>
                      {item.label}
                    </NavLink>
                  ))}
                  {!signedIn && (
                    <Button asChild className="mt-2">
                      <Link to="/login?next=%2Fonboarding%2Frole%3Fintent%3Dmentor" onClick={() => setOpen(false)}>
                        Become a mentor
                      </Link>
                    </Button>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main id="main" className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t">
        <div className="mx-auto grid w-full grid-cols-1 gap-8 px-4 py-10 sm:px-6 md:grid-cols-[1.5fr_1fr_1fr] lg:px-10 xl:px-16">
          <div className="space-y-3">
            <Logo />
            <p className="max-w-xs text-sm text-muted-foreground">
              One-to-one mentoring with verified professionals across India.
            </p>
            <ApiStatus />
          </div>
          <FooterColumn
            title="Product"
            links={[
              ['/explore', 'Explore mentors'],
              ['/pricing', 'Pricing'],
              ['/faq', 'FAQ'],
              ['/about', 'About'],
            ]}
          />
          <FooterColumn
            title="Legal"
            links={[
              ['/terms', 'Terms of service'],
              ['/privacy', 'Privacy policy'],
              ['/refund-policy', 'Refund policy'],
            ]}
          />
        </div>
        <div className="border-t py-4 text-center text-xs text-muted-foreground">
          © {new Date().getFullYear()} OneMentor
        </div>
      </footer>
    </div>
  )
}

function FooterColumn({ title, links }) {
  return (
    <div>
      <h2 className="mb-3 text-sm font-semibold">{title}</h2>
      <ul className="space-y-2 text-sm">
        {links.map(([to, label]) => (
          <li key={to}>
            <Link to={to} className="text-muted-foreground hover:text-foreground">
              {label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}
